import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import {
  adminListUsers,
  adminCreateUser,
  adminUpdateUser,
  adminSetUserRole,
  adminSetUserLevel,
  adminDeleteUser,
  adminResetUserProgress,
  adminResetUserModuleProgress,
  adminSendResetLink,
  adminSetUserPassword,
  adminBulkDelete,
  adminBulkAssign,
  adminBulkUpload,
  adminListLocales,
  listModules,
} from "@/lib/apiClient";
import { useAuth } from "@/contexts/AuthContext";
import {
  Shield, User as UserIcon, Sparkles, Search, Trash2, RotateCcw,
  UserPlus, Upload, Filter, X, Download, Pencil, MoreHorizontal,
  GraduationCap, Headphones, Users as UsersIcon, AlertTriangle,
  Mail, KeyRound, Languages,
} from "lucide-react";

const ROLES = ["admin", "support", "mentor", "principal", "teacher", "translator", "student"];
const ROLE_BADGE = {
  admin:      { bg: "bg-[#064E3B]",  text: "text-white",   icon: Shield, label: "Admin" },
  support:    { bg: "bg-[#7C3AED]",  text: "text-white",   icon: Headphones, label: "Support" },
  principal:  { bg: "bg-[#B45309]",  text: "text-white",   icon: UsersIcon, label: "Principal" },
  mentor:     { bg: "bg-[#0F766E]",  text: "text-white",   icon: GraduationCap, label: "Mentor" },
  teacher:    { bg: "bg-[#D97706]",  text: "text-white",   icon: GraduationCap, label: "Teacher (school)" },
  translator: { bg: "bg-[#0EA5E9]",  text: "text-white",   icon: Languages, label: "Translator" },
  student:    { bg: "bg-[#E7E0D2]",  text: "text-[#1F2937]", icon: UserIcon, label: "Student" },
};

// Translator is the only role carrying `assigned_locales` — reuses the
// same locale list the Translations admin page already fetches via
// GET /admin/i18n/locales, never a new/separate language format.
function localeLabel(locales, code) {
  if (!code) return null;
  const l = locales.find((x) => x.code === code);
  return l ? `${l.native} (${l.label})` : code;
}

function localeLabels(locales, codes) {
  return (codes || []).map((c) => localeLabel(locales, c)).filter(Boolean).join(", ");
}

// Shared multi-select control for a Translator's assigned languages — a
// scrollable checkbox list rather than a native multi-select, since it's
// used inside both a small popup menu and full-size modals.
function LocaleCheckboxList({ locales, selected, onChange, testId }) {
  const toggle = (code) => {
    onChange(selected.includes(code) ? selected.filter((c) => c !== code) : [...selected, code]);
  };
  return (
    <div className="max-h-56 overflow-y-auto rounded-xl border border-[#E7E0D2] divide-y divide-[#E7E0D2]">
      {locales.map((l) => (
        <label key={l.code} className="flex items-center gap-2 px-3 py-1.5 text-xs cursor-pointer hover:bg-[#FDFBF7]">
          <input
            type="checkbox"
            data-testid={testId ? `${testId}-${l.code}` : undefined}
            checked={selected.includes(l.code)}
            onChange={() => toggle(l.code)}
          />
          {l.native} ({l.label})
        </label>
      ))}
    </div>
  );
}

export default function AdminUsers() {
  const { user: me } = useAuth();
  const actorRole = me?.role || "admin";
  const isAdmin = actorRole === "admin";
  const canMutate = actorRole === "admin" || actorRole === "support";

  // List + filters
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [roleCounts, setRoleCounts] = useState({});
  const [level2Count, setLevel2Count] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filterRole, setFilterRole] = useState("");
  const [filterLevel, setFilterLevel] = useState(0);

  // Selection
  const [selected, setSelected] = useState(() => new Set());
  const toggleSel = (id) => setSelected((s) => {
    const next = new Set(s);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const clearSel = () => setSelected(new Set());
  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.user_id));
  const toggleAll = () => setSelected(() => allSelected ? new Set() : new Set(rows.map((r) => r.user_id)));

  // Modals
  const [showCreate, setShowCreate] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const [showBulkAssign, setShowBulkAssign] = useState(false);
  const [editing, setEditing] = useState(null);     // row currently being edited
  const [settingPw, setSettingPw] = useState(null); // row currently having its password set
  const [resettingModule, setResettingModule] = useState(null); // row currently picking a module to reset

  // Fetched once here (not per-modal) since Create/Bulk-assign/RowMenu all
  // need the same list to populate a Translator's "Assigned Language" field.
  const [locales, setLocales] = useState([]);
  useEffect(() => { adminListLocales().then((d) => setLocales(d.locales || [])).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminListUsers({ q, role: filterRole, level: filterLevel });
      setRows(data.users || []);
      setTotal(data.total || 0);
      setRoleCounts(data.role_counts || {});
      setLevel2Count(data.level2_count || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load users");
    } finally {
      setLoading(false);
    }
  }, [q, filterRole, filterLevel]);

  useEffect(() => { load(); }, [load]);

  // Debounced search
  const searchTimer = useRef(null);
  const onSearch = (v) => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setQ(v), 250);
  };

  const downloadCsv = () => {
    const header = "name,email,role,level,modules_passed,fluency_score,created_at\n";
    const body = rows.map((u) => [
      JSON.stringify(u.name ?? ""), JSON.stringify(u.email ?? ""),
      u.role, u.level, u.modules_passed ?? 0, u.fluency_score ?? 0, u.created_at ?? ""
    ].join(",")).join("\n");
    const blob = new Blob([header + body], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `users-${Date.now()}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  // Server-computed across every user matching the current filter, not just
  // the loaded page (`rows` is capped at 200-500 by `limit`) — the summary
  // line needs true totals, and student signup volume dominating the most
  // recent page used to make admin/support/teacher read as 0 even when
  // real accounts of those roles existed. See GET /admin/users' role_counts.
  const counts = {
    admin: roleCounts.admin || 0,
    support: roleCounts.support || 0,
    principal: roleCounts.principal || 0,
    mentor: roleCounts.mentor || 0,
    teacher: roleCounts.teacher || 0,
    translator: roleCounts.translator || 0,
    student: roleCounts.student || 0,
    l2: level2Count,
  };

  return (
    <div data-testid="admin-users" className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-[#1F2937]">User Management</h1>
          <div className="mt-1 text-sm text-[#4B5563]">
            {total} total · {counts.admin} admin · {counts.support} support · {counts.mentor} mentor · {counts.principal} principal · {counts.teacher} teacher · {counts.translator} translator · {counts.student} student · {counts.l2} on L2
            {!canMutate && <span className="ml-2 px-2 py-0.5 rounded-full bg-[#FFF7ED] border border-[#FED7AA] text-[#B45309] text-[11px]">read-only ({actorRole})</span>}
          </div>
        </div>
        {canMutate && (
          <div className="flex items-center gap-2 flex-wrap">
            <button data-testid="open-add-user" onClick={() => setShowCreate(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#064E3B] hover:bg-[#047857] text-white text-sm font-medium transition">
              <UserPlus className="w-4 h-4" /> Add user
            </button>
            <button data-testid="open-bulk-upload" onClick={() => setShowBulkUpload(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#E7E0D2] hover:border-[#064E3B] text-sm text-[#1F2937] transition">
              <Upload className="w-4 h-4" /> Bulk upload (CSV)
            </button>
            <button data-testid="export-csv" onClick={downloadCsv}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#E7E0D2] hover:border-[#064E3B] text-sm text-[#1F2937] transition">
              <Download className="w-4 h-4" /> Export
            </button>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="rounded-2xl bg-white border border-[#E7E0D2] p-4 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-[#4B5563]" />
          <input
            data-testid="users-search"
            placeholder="Search by name or email…"
            onChange={(e) => onSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-full border border-[#E7E0D2] bg-[#FDFBF7] focus:outline-none focus:border-[#064E3B] text-sm"
          />
        </div>
        <div className="inline-flex items-center gap-1.5 text-xs text-[#4B5563]"><Filter className="w-3.5 h-3.5" /> Role:</div>
        <select data-testid="filter-role" value={filterRole} onChange={(e) => setFilterRole(e.target.value)}
          className="px-3 py-2 rounded-full border border-[#E7E0D2] bg-[#FDFBF7] text-sm focus:outline-none focus:border-[#064E3B]">
          <option value="">All</option>
          {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <div className="inline-flex items-center gap-1.5 text-xs text-[#4B5563]">Level:</div>
        <select data-testid="filter-level" value={filterLevel} onChange={(e) => setFilterLevel(Number(e.target.value))}
          className="px-3 py-2 rounded-full border border-[#E7E0D2] bg-[#FDFBF7] text-sm focus:outline-none focus:border-[#064E3B]">
          <option value={0}>All</option>
          <option value={1}>L1</option>
          <option value={2}>L2</option>
        </select>
      </div>

      {/* Bulk toolbar */}
      {selected.size > 0 && canMutate && (
        <div data-testid="bulk-toolbar"
          className="sticky top-4 z-10 rounded-2xl bg-[#064E3B] text-white p-3 px-4 flex items-center gap-3 flex-wrap shadow-lg">
          <div className="font-medium">{selected.size} selected</div>
          <button data-testid="bulk-clear" onClick={clearSel}
            className="ml-1 inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-xs">
            <X className="w-3.5 h-3.5" /> Clear
          </button>
          <div className="flex-1" />
          <button data-testid="bulk-assign" onClick={() => setShowBulkAssign(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#D97706] hover:bg-[#B45309] text-xs font-medium">
            <Sparkles className="w-3.5 h-3.5" /> Assign role / level
          </button>
          <button data-testid="bulk-delete" onClick={() => setShowBulkDelete(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#EF4444] hover:bg-[#DC2626] text-xs font-medium">
            <Trash2 className="w-3.5 h-3.5" /> Delete
          </button>
        </div>
      )}

      {/* Table */}
      <div className="rounded-2xl bg-white border border-[#E7E0D2] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#FDFBF7] text-[#4B5563] text-left text-xs uppercase tracking-wider">
            <tr>
              <th className="px-4 py-3 w-10">
                {canMutate && (
                  <input
                    type="checkbox"
                    data-testid="select-all"
                    checked={allSelected}
                    onChange={toggleAll}
                  />
                )}
              </th>
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Email</th>
              <th className="px-4 py-3 font-medium">Role</th>
              <th className="px-4 py-3 font-medium">Tier</th>
              <th className="px-4 py-3 font-medium">Progress</th>
              <th className="px-4 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="text-center py-10 text-[#4B5563]">Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-[#4B5563]">No users match these filters.</td></tr>}
            {!loading && rows.map((u) => {
              const level = Number(u.level || 1);
              const isMe = u.user_id === me?.user_id;
              const blockSupport = actorRole === "support" && u.role === "admin";
              const Badge = ROLE_BADGE[u.role] || ROLE_BADGE.student;
              const Icon = Badge.icon;
              return (
                <tr key={u.user_id} className={`border-t border-[#E7E0D2] hover:bg-[#FDFBF7] ${selected.has(u.user_id) ? "bg-[#FFF7ED]" : ""}`}
                  data-testid={`row-${u.user_id}`}>
                  <td className="px-4 py-3">
                    {canMutate && !blockSupport && !isMe && (
                      <input
                        type="checkbox"
                        data-testid={`sel-${u.user_id}`}
                        checked={selected.has(u.user_id)}
                        onChange={() => toggleSel(u.user_id)}
                      />
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {u.picture ? <img src={u.picture} className="w-7 h-7 rounded-full object-cover" alt="" /> : <UserIcon className="w-5 h-5 text-[#4B5563]" />}
                      <span className="font-medium text-[#1F2937]">{u.name}</span>
                      {isMe && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#FFF7ED] text-[#B45309] border border-[#FED7AA]">you</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-[#4B5563]">{u.email}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${Badge.bg} ${Badge.text}`}>
                      <Icon className="w-3 h-3" /> {Badge.label}
                    </span>
                    {u.role === "translator" && u.assigned_locales?.length > 0 && (
                      <div className="mt-1 text-[11px] text-[#4B5563]" data-testid={`translator-locale-${u.user_id}`}>
                        {localeLabels(locales, u.assigned_locales)}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3" data-testid={`tier-${u.user_id}`}>
                    {level >= 2 ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FFF7ED] border border-[#FED7AA] text-[#B45309] text-xs">
                        <Sparkles className="w-3 h-3" /> L2
                      </span>
                    ) : (
                      <span className="text-[#4B5563] text-xs">L1</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-[#4B5563]">
                    <span className="font-medium text-[#1F2937]">{u.modules_passed || 0}</span>/9 passed
                    {(u.fluency_score || 0) > 0 && <span className="ml-2 text-[#D97706]">{u.fluency_score}%</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <RowMenu u={u} actorRole={actorRole} isAdmin={isAdmin} canMutate={canMutate} isMe={isMe} locales={locales}
                      onChange={load} onEdit={() => setEditing(u)} onSetPassword={() => setSettingPw(u)}
                      onResetModule={() => setResettingModule(u)} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Modals */}
      {showCreate && <CreateUserModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} isAdmin={isAdmin} locales={locales} />}
      {showBulkUpload && <BulkUploadModal onClose={() => setShowBulkUpload(false)} onDone={() => { setShowBulkUpload(false); load(); }} />}
      {showBulkDelete && <BulkDeleteModal selected={selected} onClose={() => setShowBulkDelete(false)} onDone={() => { clearSel(); setShowBulkDelete(false); load(); }} />}
      {showBulkAssign && <BulkAssignModal selected={selected} isAdmin={isAdmin} locales={locales} onClose={() => setShowBulkAssign(false)} onDone={() => { clearSel(); setShowBulkAssign(false); load(); }} />}
      {editing && <EditUserModal user={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {settingPw && <SetPasswordModal user={settingPw} onClose={() => setSettingPw(null)} onDone={() => { setSettingPw(null); load(); }} />}
      {resettingModule && <ResetModuleModal user={resettingModule} onClose={() => setResettingModule(null)} onDone={() => { setResettingModule(null); load(); }} />}
    </div>
  );
}

/* ---------------- Row Menu ----------------
   The dropdown is rendered through a Portal anchored to `document.body` so
   that the table wrapper's rounded `overflow-hidden` (needed for clean
   corner clipping) doesn't clip the menu. Position is computed from the
   trigger button's `getBoundingClientRect()` and the menu auto-flips
   upward when the trigger is in the bottom half of the viewport. */
function RowMenu({ u, actorRole, isAdmin, canMutate, isMe, locales, onChange, onEdit, onSetPassword, onResetModule }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);     // { top, left, flip }
  // Assigning/changing a Translator's languages is a two-step flow within
  // the same popup (pick role → pick languages → confirm) rather than a
  // separate modal, since it's just one extra field on the existing
  // "Set role" action — reuses the same /admin/users/{id}/role endpoint.
  const [localePicker, setLocalePicker] = useState(false);
  const [pickedLocales, setPickedLocales] = useState([]);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  // Click-outside + escape to close.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        menuRef.current && !menuRef.current.contains(e.target)
      ) setOpen(false);
    };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    // Closes the popup when the page/table scrolls the anchored trigger out
    // from under it — but scrolling *inside* the popup itself (e.g. the
    // locale checklist, which is taller than its viewport) must not count
    // as that, otherwise picking several languages closes the menu the
    // instant the list needs to scroll to reveal more options.
    const onScroll = (e) => {
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [open]);

  // Compute position relative to trigger when menu opens.
  const computePos = useCallback(() => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    const menuH = 280;  // approximate
    const menuW = 224;  // 56 * 4 = w-56
    const willOverflowBottom = r.bottom + menuH > window.innerHeight - 12;
    const top = willOverflowBottom ? Math.max(8, r.top - menuH) : r.bottom + 4;
    const left = Math.min(window.innerWidth - menuW - 8, Math.max(8, r.right - menuW));
    setPos({ top, left });
  }, []);

  const toggle = () => {
    if (!open) { computePos(); setLocalePicker(false); }
    setOpen((v) => !v);
  };

  const blockSupport = actorRole === "support" && u.role === "admin";

  const setRole = async (role, assignedLocales) => {
    const label = assignedLocales?.length ? ` (${localeLabels(locales, assignedLocales)})` : "";
    if (!window.confirm(`Set ${u.email} role to "${role}"${label}?`)) return;
    try {
      await adminSetUserRole(u.user_id, role, assignedLocales);
      toast.success(`Role set to ${role}${label}`);
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const openLocalePicker = () => {
    setPickedLocales(u.assigned_locales || []);
    setLocalePicker(true);
  };

  const confirmTranslatorLocale = () => {
    if (pickedLocales.length === 0) { toast.error("Pick at least one language first"); return; }
    setRole("translator", pickedLocales);
    setLocalePicker(false);
    setOpen(false);
  };

  const toggleLevel = async () => {
    const cur = Number(u.level || 1);
    const next = cur >= 2 ? 1 : 2;
    if (next === 2 && !window.confirm(`Mark ${u.email} as L2 ($5 paid)? Confirm only after verifying payment.`)) return;
    try {
      await adminSetUserLevel(u.user_id, next);
      toast.success(next === 2 ? "Upgraded to L2" : "Downgraded to L1");
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const sendResetLink = async () => {
    if (!window.confirm(`Send a password reset link to ${u.email}?`)) return;
    try {
      const res = await adminSendResetLink(u.user_id);
      toast[res.email_sent ? "success" : "message"](
        res.email_sent ? `Reset link sent to ${u.email}` : "Reset link generated, but email delivery isn't configured — check server logs."
      );
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const reset = async () => {
    if (!window.confirm(`Reset ALL module progress for ${u.email}? This wipes attempts, scores, recitations and certificates.`)) return;
    try {
      await adminResetUserProgress(u.user_id);
      toast.success("Progress reset");
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const del = async () => {
    if (!window.confirm(`Delete ${u.email}? This is irreversible.`)) return;
    try {
      await adminDeleteUser(u.user_id);
      toast.success("User deleted");
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!canMutate) {
    return <span className="text-xs text-[#9CA3AF]">view only</span>;
  }
  if (blockSupport) {
    return <span className="text-xs text-[#9CA3AF]" title="Support cannot modify Admin accounts">locked</span>;
  }
  return (
    <>
      <button ref={triggerRef} data-testid={`row-menu-${u.user_id}`} onClick={toggle}
        className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-white border border-[#E7E0D2] hover:border-[#064E3B] text-[#1F2937]">
        <MoreHorizontal className="w-4 h-4" />
      </button>
      {open && pos && createPortal(
        <div ref={menuRef}
          data-testid={`row-menu-popup-${u.user_id}`}
          style={{ position: "fixed", top: pos.top, left: pos.left, width: 224 }}
          className="rounded-xl bg-white border border-[#E7E0D2] shadow-xl z-[60] p-1.5 text-left">
        {localePicker ? (
          <div className="p-2" data-testid={`translator-locale-picker-${u.user_id}`}>
            <div className="px-1 py-1 text-[10px] uppercase tracking-wider text-[#4B5563]">Assigned languages</div>
            <div className="mb-2">
              <LocaleCheckboxList
                locales={locales}
                selected={pickedLocales}
                onChange={setPickedLocales}
                testId={`translator-locale-option-${u.user_id}`}
              />
            </div>
            <div className="flex items-center gap-2">
              <button data-testid={`translator-locale-confirm-${u.user_id}`} onClick={confirmTranslatorLocale}
                className="flex-1 text-xs px-2.5 py-1.5 rounded-full bg-[#064E3B] text-white hover:bg-[#047857]">Confirm</button>
              <button onClick={() => setLocalePicker(false)}
                className="flex-1 text-xs px-2.5 py-1.5 rounded-full border border-[#E7E0D2] text-[#4B5563] hover:border-[#064E3B]">Cancel</button>
            </div>
          </div>
        ) : (
          <>
          <MenuItem icon={Pencil} label="Edit name / email" onClick={() => { onEdit(); setOpen(false); }} />
          <MenuItem icon={Mail} label="Send reset link" onClick={() => { sendResetLink(); setOpen(false); }} />
          <MenuItem icon={KeyRound} label="Set password" onClick={() => { onSetPassword(); setOpen(false); }} />
          <MenuItem icon={Sparkles} label={Number(u.level || 1) >= 2 ? "Downgrade to L1" : "Upgrade to L2"} onClick={() => { toggleLevel(); setOpen(false); }} />
          {isAdmin && (
            <>
              <div className="my-1 border-t border-[#E7E0D2]" />
              <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-[#4B5563]">Set role</div>
              {ROLES.filter((r) => r !== u.role).map((r) => (
                <MenuItem key={r} icon={ROLE_BADGE[r].icon} label={`→ ${ROLE_BADGE[r].label}`}
                  onClick={() => {
                    if (r === "translator") { openLocalePicker(); return; }
                    setRole(r); setOpen(false);
                  }} />
              ))}
              {u.role === "translator" && (
                <MenuItem icon={Languages} label="Change assigned languages" onClick={openLocalePicker} />
              )}
            </>
          )}
          <div className="my-1 border-t border-[#E7E0D2]" />
          <MenuItem icon={RotateCcw} label="Reset module progress" onClick={() => { reset(); setOpen(false); }} />
          <MenuItem icon={RotateCcw} label="Reset one module…" onClick={() => { onResetModule(); setOpen(false); }} />
          {!isMe && <MenuItem icon={Trash2} danger label="Delete user" onClick={() => { del(); setOpen(false); }} />}
          </>
        )}
        </div>,
        document.body
      )}
    </>
  );
}

function MenuItem({ icon: Icon, label, onClick, danger }) {
  return (
    <button onClick={onClick}
      className={`w-full flex items-center gap-2 px-3 py-2 text-xs rounded-lg ${danger ? "text-[#DC2626] hover:bg-[#FEE2E2]" : "text-[#1F2937] hover:bg-[#FDFBF7]"}`}>
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  );
}

/* ---------------- Modals ---------------- */
function ModalShell({ title, children, onClose, max = "max-w-md" }) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
      <div className={`w-full ${max} bg-white rounded-2xl shadow-2xl`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#E7E0D2]">
          <div className="font-heading text-lg text-[#1F2937]">{title}</div>
          <button onClick={onClose} className="text-[#4B5563] hover:text-[#1F2937]"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children, hint }) {
  return (
    <label className="block text-xs uppercase tracking-wider text-[#4B5563] mt-3">
      <div>{label}</div>
      <div className="mt-1 normal-case tracking-normal">{children}</div>
      {hint && <div className="mt-1 text-[11px] text-[#4B5563] normal-case tracking-normal">{hint}</div>}
    </label>
  );
}

function CreateUserModal({ onClose, onCreated, isAdmin, locales }) {
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "student", level: 1, assigned_locales: [] });
  const [busy, setBusy] = useState(false);
  const [tempPw, setTempPw] = useState(null);
  const isTranslatorRole = form.role === "translator";
  const submit = async (e) => {
    e.preventDefault();
    if (isTranslatorRole && form.assigned_locales.length === 0) {
      toast.error("Pick at least one assigned language for the Translator role");
      return;
    }
    setBusy(true);
    try {
      const res = await adminCreateUser({
        ...form,
        level: Number(form.level),
        password: form.password || undefined,
        assigned_locales: isTranslatorRole ? form.assigned_locales : undefined,
      });
      setTempPw(res.temp_password);
      toast.success("User created");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create");
    } finally { setBusy(false); }
  };
  if (tempPw) {
    return (
      <ModalShell title="User created" onClose={() => { onCreated(); }}>
        <p className="text-sm text-[#1F2937]">Temporary password for <b>{form.email}</b>:</p>
        <div className="mt-3 px-4 py-3 rounded-xl bg-[#FDFBF7] border border-[#E7E0D2] font-mono text-sm" data-testid="create-temp-pw">{tempPw}</div>
        <p className="mt-2 text-xs text-[#4B5563]">Share this with the user. They should change it after first login.</p>
        <div className="mt-5 flex justify-end">
          <button onClick={onCreated} className="px-4 py-2 rounded-full bg-[#064E3B] text-white text-sm">Done</button>
        </div>
      </ModalShell>
    );
  }
  return (
    <ModalShell title="Add user" onClose={onClose}>
      <form onSubmit={submit} data-testid="create-user-form">
        <Field label="Name"><input data-testid="create-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="w-full px-3 py-2 rounded-xl border border-[#E7E0D2] focus:outline-none focus:border-[#064E3B] text-sm" /></Field>
        <Field label="Email"><input data-testid="create-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
          className="w-full px-3 py-2 rounded-xl border border-[#E7E0D2] focus:outline-none focus:border-[#064E3B] text-sm" /></Field>
        <Field label="Password" hint="Leave blank to auto-generate a 10-char temp password.">
          <input data-testid="create-password" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
            className="w-full px-3 py-2 rounded-xl border border-[#E7E0D2] focus:outline-none focus:border-[#064E3B] text-sm" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Role">
            <select data-testid="create-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="w-full px-3 py-2 rounded-xl border border-[#E7E0D2] focus:outline-none focus:border-[#064E3B] text-sm">
              {ROLES.filter((r) => (r !== "admin" && r !== "translator" && r !== "principal" && r !== "teacher") || isAdmin).map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </Field>
          <Field label="Level">
            <select data-testid="create-level" value={form.level} onChange={(e) => setForm({ ...form, level: Number(e.target.value) })}
              className="w-full px-3 py-2 rounded-xl border border-[#E7E0D2] focus:outline-none focus:border-[#064E3B] text-sm">
              <option value={1}>L1 (free)</option>
              <option value={2}>L2 ($5 paid)</option>
            </select>
          </Field>
        </div>
        {isTranslatorRole && (
          <Field label="Assigned Languages">
            <LocaleCheckboxList
              locales={locales}
              selected={form.assigned_locales}
              onChange={(assigned_locales) => setForm({ ...form, assigned_locales })}
              testId="create-assigned-locale"
            />
          </Field>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-full bg-white border border-[#E7E0D2] text-sm text-[#1F2937]">Cancel</button>
          <button data-testid="create-submit" type="submit" disabled={busy}
            className="px-4 py-2 rounded-full bg-[#064E3B] hover:bg-[#047857] text-white text-sm disabled:opacity-60">
            {busy ? "Creating…" : "Create user"}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function EditUserModal({ user, onClose, onSaved }) {
  const [form, setForm] = useState({ name: user.name || "", email: user.email || "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await adminUpdateUser(user.user_id, form);
      toast.success("User updated");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update");
    } finally { setBusy(false); }
  };
  return (
    <ModalShell title="Edit user" onClose={onClose}>
      <form onSubmit={submit} data-testid="edit-user-form">
        <Field label="Name"><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="w-full px-3 py-2 rounded-xl border border-[#E7E0D2] focus:outline-none focus:border-[#064E3B] text-sm" /></Field>
        <Field label="Email"><input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
          className="w-full px-3 py-2 rounded-xl border border-[#E7E0D2] focus:outline-none focus:border-[#064E3B] text-sm" /></Field>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-full bg-white border border-[#E7E0D2] text-sm text-[#1F2937]">Cancel</button>
          <button type="submit" disabled={busy}
            className="px-4 py-2 rounded-full bg-[#064E3B] hover:bg-[#047857] text-white text-sm disabled:opacity-60">
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function SetPasswordModal({ user, onClose, onDone }) {
  const [newPassword, setNewPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // the resulting plaintext password, once set
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await adminSetUserPassword(user.user_id, newPassword.trim() || undefined);
      setResult(res.new_password);
      toast.success("Password updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update password");
    } finally { setBusy(false); }
  };
  if (result) {
    return (
      <ModalShell title="Password updated" onClose={onDone}>
        <p className="text-sm text-[#1F2937]">New password for <b>{user.email}</b>:</p>
        <div className="mt-3 px-4 py-3 rounded-xl bg-[#FDFBF7] border border-[#E7E0D2] font-mono text-sm" data-testid="set-password-result">{result}</div>
        <p className="mt-2 text-xs text-[#4B5563]">Share this with the user via a secure channel. Any active sessions have been signed out.</p>
        <div className="mt-5 flex justify-end">
          <button onClick={onDone} className="px-4 py-2 rounded-full bg-[#064E3B] text-white text-sm">Done</button>
        </div>
      </ModalShell>
    );
  }
  return (
    <ModalShell title={`Set password — ${user.email}`} onClose={onClose}>
      <form onSubmit={submit} data-testid="set-password-form">
        <Field label="New password" hint="Leave blank to auto-generate a 10-char password. Min. 8 characters if set manually.">
          <input data-testid="set-password-input" type="text" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
            className="w-full px-3 py-2 rounded-xl border border-[#E7E0D2] focus:outline-none focus:border-[#064E3B] text-sm" />
        </Field>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-full bg-white border border-[#E7E0D2] text-sm text-[#1F2937]">Cancel</button>
          <button data-testid="set-password-submit" type="submit" disabled={busy}
            className="px-4 py-2 rounded-full bg-[#064E3B] hover:bg-[#047857] text-white text-sm disabled:opacity-60">
            {busy ? "Saving…" : "Set password"}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function ResetModuleModal({ user, onClose, onDone }) {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    listModules().then((mods) => {
      setModules(mods);
      if (mods.length) setCode(mods[0].code);
    }).finally(() => setLoading(false));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!code) return;
    const m = modules.find((x) => x.code === code);
    if (!window.confirm(`Reset progress on "${m?.name || code}" for ${user.email}? This wipes that module's attempts, scores and recitations only.`)) return;
    setBusy(true);
    try {
      await adminResetUserModuleProgress(user.user_id, code);
      toast.success(`${m?.name || code} progress reset`);
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reset module");
    } finally { setBusy(false); }
  };

  return (
    <ModalShell title={`Reset one module — ${user.email}`} onClose={onClose}>
      <form onSubmit={submit} data-testid="reset-module-form">
        <Field label="Module" hint="Only this module's progress, attempts, scores and recitations are cleared — the other modules are untouched.">
          {loading ? (
            <div className="text-sm text-[#4B5563]">Loading modules…</div>
          ) : (
            <select data-testid="reset-module-select" value={code} onChange={(e) => setCode(e.target.value)}
              className="w-full px-3 py-2 rounded-xl border border-[#E7E0D2] focus:outline-none focus:border-[#064E3B] text-sm">
              {modules.map((m) => (
                <option key={m.code} value={m.code}>{m.n != null ? `${m.n}. ` : ""}{m.name}</option>
              ))}
            </select>
          )}
        </Field>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-full bg-white border border-[#E7E0D2] text-sm text-[#1F2937]">Cancel</button>
          <button data-testid="reset-module-submit" type="submit" disabled={busy || loading || !code}
            className="px-4 py-2 rounded-full bg-[#064E3B] hover:bg-[#047857] text-white text-sm disabled:opacity-60">
            {busy ? "Resetting…" : "Reset module"}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function BulkUploadModal({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const submit = async () => {
    if (!file) return toast.error("Choose a CSV file");
    setBusy(true);
    try {
      const res = await adminBulkUpload(file);
      setReport(res);
      toast.success(`${res.total_created} created · ${res.total_skipped} skipped`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };
  if (report) {
    return (
      <ModalShell title="Bulk upload report" max="max-w-2xl" onClose={() => { onDone(); }}>
        <div className="text-sm text-[#1F2937]">
          ✅ <b>{report.total_created}</b> created &nbsp;·&nbsp; ⚠️ <b>{report.total_skipped}</b> skipped
        </div>
        {report.created.length > 0 && (
          <div className="mt-4">
            <div className="text-xs uppercase tracking-wider text-[#4B5563] mb-2">Created — copy these temp passwords</div>
            <div className="max-h-56 overflow-auto rounded-xl border border-[#E7E0D2]">
              <table className="w-full text-xs">
                <thead className="bg-[#FDFBF7]"><tr><th className="px-3 py-2 text-left">Email</th><th className="px-3 py-2 text-left">Role</th><th className="px-3 py-2 text-left">Temp password</th></tr></thead>
                <tbody>
                  {report.created.map((r) => (
                    <tr key={r.email} className="border-t border-[#E7E0D2]">
                      <td className="px-3 py-2 font-mono">{r.email}</td>
                      <td className="px-3 py-2">{r.role}</td>
                      <td className="px-3 py-2 font-mono text-[#D97706]">{r.temp_password}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {report.skipped.length > 0 && (
          <div className="mt-4">
            <div className="text-xs uppercase tracking-wider text-[#4B5563] mb-2">Skipped</div>
            <ul className="text-xs text-[#4B5563] space-y-1 max-h-32 overflow-auto">
              {report.skipped.map((s, i) => <li key={i}><span className="font-mono">{s.email}</span> — {s.reason}</li>)}
            </ul>
          </div>
        )}
        <div className="mt-5 flex justify-end">
          <button onClick={onDone} className="px-4 py-2 rounded-full bg-[#064E3B] text-white text-sm">Done</button>
        </div>
      </ModalShell>
    );
  }
  return (
    <ModalShell title="Bulk upload users (CSV)" onClose={onClose}>
      <div className="text-xs text-[#4B5563]">
        CSV columns: <span className="font-mono">name</span>, <span className="font-mono">email</span> (required); optional: <span className="font-mono">password</span>, <span className="font-mono">role</span> (admin/support/teacher/translator/student), <span className="font-mono">assigned_locales</span> (required if role=translator — one or more codes separated by "," or ";", e.g. "ur;ms"), <span className="font-mono">level</span> (1/2).
      </div>
      <div className="mt-4">
        <input data-testid="bulk-upload-file" type="file" accept=".csv,text/csv" onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="w-full text-sm" />
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <button type="button" onClick={onClose} className="px-4 py-2 rounded-full bg-white border border-[#E7E0D2] text-sm text-[#1F2937]">Cancel</button>
        <button data-testid="bulk-upload-submit" onClick={submit} disabled={busy || !file}
          className="px-4 py-2 rounded-full bg-[#064E3B] hover:bg-[#047857] text-white text-sm disabled:opacity-60">
          {busy ? "Uploading…" : "Upload"}
        </button>
      </div>
    </ModalShell>
  );
}

function BulkDeleteModal({ selected, onClose, onDone }) {
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (token.trim().toUpperCase() !== "DELETE") return toast.error("Type DELETE to confirm");
    setBusy(true);
    try {
      const res = await adminBulkDelete([...selected], token);
      toast.success(`${res.deleted} deleted${res.skipped?.length ? ` · ${res.skipped.length} skipped` : ""}`);
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk delete failed");
    } finally { setBusy(false); }
  };
  return (
    <ModalShell title="Bulk delete users" onClose={onClose}>
      <div className="flex items-start gap-3 p-3 rounded-xl bg-[#FEE2E2] text-[#991B1B]">
        <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          You&apos;re about to delete <b>{selected.size}</b> user{selected.size === 1 ? "" : "s"}. This wipes progress, recitations, certificates, and resets. Irreversible.
        </div>
      </div>
      <Field label='Type "DELETE" to confirm'>
        <input data-testid="bulk-delete-token" value={token} onChange={(e) => setToken(e.target.value)} placeholder="DELETE"
          className="w-full px-3 py-2 rounded-xl border border-[#E7E0D2] focus:outline-none focus:border-[#EF4444] text-sm font-mono" />
      </Field>
      <div className="mt-5 flex justify-end gap-2">
        <button onClick={onClose} className="px-4 py-2 rounded-full bg-white border border-[#E7E0D2] text-sm text-[#1F2937]">Cancel</button>
        <button data-testid="bulk-delete-submit" onClick={submit} disabled={busy}
          className="px-4 py-2 rounded-full bg-[#EF4444] hover:bg-[#DC2626] text-white text-sm disabled:opacity-60">
          {busy ? "Deleting…" : `Delete ${selected.size}`}
        </button>
      </div>
    </ModalShell>
  );
}

function BulkAssignModal({ selected, isAdmin, locales, onClose, onDone }) {
  const [role, setRole] = useState("");
  const [assignedLocales, setAssignedLocales] = useState([]);
  const [level, setLevel] = useState(0);
  const [busy, setBusy] = useState(false);
  const isTranslatorRole = role === "translator";
  const submit = async () => {
    if (!role && !level) return toast.error("Pick a role or level");
    if (isTranslatorRole && assignedLocales.length === 0) return toast.error("Pick at least one assigned language for the Translator role");
    setBusy(true);
    try {
      const payload = {};
      if (role) payload.role = role;
      if (isTranslatorRole) payload.assigned_locales = assignedLocales;
      if (level) payload.level = level;
      const res = await adminBulkAssign([...selected], payload);
      toast.success(`Updated ${res.updated}${res.skipped?.length ? ` · ${res.skipped.length} skipped` : ""}`);
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk assign failed");
    } finally { setBusy(false); }
  };
  return (
    <ModalShell title={`Assign to ${selected.size} user(s)`} onClose={onClose}>
      {isAdmin && (
        <Field label="Role (admin only)">
          <select data-testid="bulk-assign-role" value={role} onChange={(e) => setRole(e.target.value)}
            className="w-full px-3 py-2 rounded-xl border border-[#E7E0D2] focus:outline-none focus:border-[#064E3B] text-sm">
            <option value="">— don&apos;t change —</option>
            {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </Field>
      )}
      {isAdmin && isTranslatorRole && (
        <Field label="Assigned Languages">
          <LocaleCheckboxList
            locales={locales}
            selected={assignedLocales}
            onChange={setAssignedLocales}
            testId="bulk-assign-locale"
          />
        </Field>
      )}
      <Field label="Level">
        <select data-testid="bulk-assign-level" value={level} onChange={(e) => setLevel(Number(e.target.value))}
          className="w-full px-3 py-2 rounded-xl border border-[#E7E0D2] focus:outline-none focus:border-[#064E3B] text-sm">
          <option value={0}>— don&apos;t change —</option>
          <option value={1}>L1 (free)</option>
          <option value={2}>L2 ($5 paid)</option>
        </select>
      </Field>
      <div className="mt-5 flex justify-end gap-2">
        <button onClick={onClose} className="px-4 py-2 rounded-full bg-white border border-[#E7E0D2] text-sm text-[#1F2937]">Cancel</button>
        <button data-testid="bulk-assign-submit" onClick={submit} disabled={busy}
          className="px-4 py-2 rounded-full bg-[#064E3B] hover:bg-[#047857] text-white text-sm disabled:opacity-60">
          {busy ? "Applying…" : "Apply"}
        </button>
      </div>
    </ModalShell>
  );
}
